import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Cross1Icon } from '@radix-ui/react-icons';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { ComponentPropsWithoutRef } from 'react';
import React from 'react';
import type { SelectInstance } from 'react-select';
import Select, { type ClassNamesConfig, type StylesConfig } from 'react-select';

const multiselectVariants = cva(
  cn(
    'flex w-full text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground relative',
    'hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 focus-within:border-primary-foreground focus-within:ring-1 focus-within:ring-primary',
    'min-h-[48px] w-full rounded-[6px] border border-border bg-background text-sm transition-colors focus-within:border-primary-foreground focus-within:ring-1',
    'focus-within:ring-primary hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
    'border-box items-center',
  ),
  {
    variants: {
      variant: {
        outlined: ['rounded-[6px] bg-background border', 'ea-input__outlined'],
        filled: ['rounded-[6px] bg-input border-none', 'ea-input__filled'],
      },
      rounded: {
        default: ['rounded-[6px]', 'ea-input__wrapper--rounded-default'],
        none: ['rounded-none', 'ea-input__wrapper--rounded-none'],
      },
    },
    defaultVariants: {
      rounded: 'default',
      variant: 'filled',
    },
  },
);

export interface MultiSelectProps
  extends
    VariantProps<typeof multiselectVariants>,
    Omit<ComponentPropsWithoutRef<typeof Select>, 'classNames' | 'onInputChange'> {
  placeholder?: string;
  before?: React.ReactNode;
  options?:
    | {
        label: string;
        value: string | number;
      }[]
    | undefined;
  stylesConfig?: StylesConfig;
  classNamesConfig?: ClassNamesConfig;
  noOptionsMessage: () => string;
  handleInputChange?: (value: string) => void;
  dropdownIndicator?: React.ReactNode;
  isLoading?: boolean;
}

export const MultiSelect = React.forwardRef<SelectInstance<any, any>, MultiSelectProps>(
  (
    {
      placeholder,
      stylesConfig,
      classNamesConfig,
      noOptionsMessage,
      handleInputChange,
      dropdownIndicator,
      isLoading,
      ...props
    },
    ref,
  ) => {
    return (
      <Select
        isMulti
        unstyled
        ref={ref}
        noOptionsMessage={noOptionsMessage}
        placeholder={placeholder}
        isLoading={!!isLoading}
        loadingMessage={() => (
          <div className="flex h-9 w-full items-center justify-center">
            <Spinner className="h-6 w-6" />
          </div>
        )}
        styles={{
          ...stylesConfig,
          menuPortal: base => ({ ...base, zIndex: 9999 }),
        }}
        classNames={{
          ...classNamesConfig,
          control: state =>
            cn(
              'rounded-none px-3 py-1 w-full !min-h-[48px] bg-transparent hover:outline-none focus:outline-none',
              'ea-multi-select-control',
              classNamesConfig?.control?.(state),
            ),
          menuList: state =>
            cn(
              'py-1 bg-white rounded-[6px] overflow-y-auto max-h-[260px]',
              'ea-multi-select-menu-list',
              classNamesConfig?.menuList?.(state),
            ),
          placeholder: state =>
            cn(
              'text-muted-foreground',
              'ea-multi-select-placeholder',
              classNamesConfig?.placeholder?.(state),
            ),
          option: state =>
            cn(
              'hover:bg-muted px-3 py-2 cursor-pointer',
              'ea-multi-select-option',
              classNamesConfig?.option?.(state),
            ),
          noOptionsMessage: state =>
            cn(
              'p-2',
              'ea-multi-select-no-options-message',
              classNamesConfig?.noOptionsMessage?.(state),
            ),
          multiValue: state =>
            cn(
              'bg-primary text-primary-foreground px-2 py-1 rounded-[6px]',
              'ea-multi-select-multi-value',
              classNamesConfig?.multiValue?.(state),
            ),
          multiValueLabel: state =>
            cn(
              'text-white text-sm',
              'ea-multi-select-multi-value-label',
              classNamesConfig?.multiValueLabel?.(state),
            ),
          multiValueRemove: state =>
            cn(
              'text-white hover:text-white hover:bg-transparent pl-1',
              'ea-multi-select-multi-value-remove',
              classNamesConfig?.multiValueRemove?.(state),
            ),
          valueContainer: state =>
            cn(
              'gap-2',
              'ea-multi-select-value-container',
              classNamesConfig?.valueContainer?.(state),
            ),
        }}
        className={cn(
          multiselectVariants({
            ...props,
            className: cn('ea-multi-select-container'),
          }),
        )}
        {...props}
        onInputChange={handleInputChange}
        components={{
          SelectContainer: ({ children, className }) => (
            <div className={cn('flex w-full flex-row gap-0', className)}>
              {props.before}
              <div className="flex-1">{children}</div>
            </div>
          ),
          ...(dropdownIndicator ? { DropdownIndicator: () => dropdownIndicator } : {}),
          ClearIndicator: ({ clearValue }) => (
            <Cross1Icon
              className={cn(
                'h-4 w-4 cursor-pointer opacity-50 hover:opacity-100',
                'ea-select__clear-icon',
              )}
              onClick={e => {
                e.stopPropagation();
                clearValue();
              }}
            />
          ),
        }}
      />
    );
  },
);

MultiSelect.displayName = 'MultiSelect';
